(() => {
  'use strict';

  document.documentElement.classList.add('gowin-premium-ui');

  const CARD_BADGE_CLASS = 'gowin-card-discount-badge';
  const PRODUCT_BADGE_CLASS = 'gowin-product-discount-badge';
  const STYLE_ID = 'gowin-discount-badge-styles';
  const ROOT_SELECTOR = '.card-wrapper, .product__info-container';
  const queuedRoots = new Set();
  let frameId = 0;

  function parseMoney(value) {
    let normalized = String(value || '')
      .replace(/\s/g, '')
      .replace(/[^0-9,.-]/g, '');

    if (!normalized) return NaN;

    const commaIndex = normalized.lastIndexOf(',');
    const dotIndex = normalized.lastIndexOf('.');

    if (commaIndex !== -1 && dotIndex !== -1) {
      normalized = commaIndex > dotIndex
        ? normalized.replace(/\./g, '').replace(',', '.')
        : normalized.replace(/,/g, '');
    } else if (commaIndex !== -1) {
      const decimals = normalized.length - commaIndex - 1;
      normalized = decimals === 2
        ? normalized.replace(',', '.')
        : normalized.replace(/,/g, '');
    } else if (dotIndex !== -1) {
      const decimals = normalized.length - dotIndex - 1;
      if (decimals !== 2) normalized = normalized.replace(/\./g, '');
    }

    return Number(normalized);
  }

  function getDiscountPercentage(priceElement) {
    if (!priceElement) return 0;

    const compareElement = priceElement.querySelector(
      '.price__sale s.price-item--regular, s.price-item--regular'
    );
    const currentElement = priceElement.querySelector(
      '.price__sale .price-item--sale, .price-item--sale'
    );

    const comparePrice = parseMoney(compareElement?.textContent);
    const currentPrice = parseMoney(currentElement?.textContent);

    if (
      !Number.isFinite(comparePrice) ||
      !Number.isFinite(currentPrice) ||
      comparePrice <= 0 ||
      currentPrice >= comparePrice
    ) {
      return 0;
    }

    return Math.max(
      1,
      Math.round(((comparePrice - currentPrice) / comparePrice) * 100)
    );
  }

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .gowin-card-discount-badge {
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        min-width: 4.8rem;
        padding: .65rem .95rem !important;
        color: #fff !important;
        background: #0f776d !important;
        border: 0 !important;
        border-radius: 999px !important;
        font-size: 1.1rem !important;
        font-weight: 850 !important;
        letter-spacing: .02em !important;
        line-height: 1 !important;
        box-shadow: 0 8px 20px rgba(15, 119, 109, .24) !important;
      }

      .gowin-product-discount-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: fit-content;
        margin: .9rem 0 0;
        padding: .75rem 1.1rem;
        color: #fff;
        background: #0f776d;
        border-radius: 999px;
        font-size: 1.25rem;
        font-weight: 820;
        letter-spacing: .01em;
        line-height: 1;
        box-shadow: 0 8px 20px rgba(15, 119, 109, .2);
      }

      @media screen and (max-width: 749px) {
        .gowin-card-discount-badge {
          min-width: 4.4rem;
          padding: .58rem .78rem !important;
          font-size: 1rem !important;
        }

        .gowin-product-discount-badge {
          margin-top: .75rem;
          font-size: 1.15rem;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function prepareCards(scope) {
    const cards = [];

    if (scope instanceof Element && scope.matches('.card-wrapper')) {
      cards.push(scope);
    }

    scope.querySelectorAll?.('.card-wrapper').forEach((card) => cards.push(card));

    cards.forEach((card) => {
      if (card.dataset.gowinCard === 'true') return;
      card.dataset.gowinCard = 'true';

      const heading = card.querySelector('.card__heading');
      const label = heading?.textContent?.trim();
      if (label) card.setAttribute('aria-label', label);
    });
  }

  function findSaleBadge(card) {
    return Array.from(
      card.querySelectorAll('.card__badge .badge:not(.badge--free-shipping)')
    ).find((candidate) => !/agotado|sold out/i.test(candidate.textContent || ''));
  }

  function restoreCardBadge(badge) {
    if (!badge) return;

    if (badge.dataset.gowinOriginalText) {
      badge.textContent = badge.dataset.gowinOriginalText;
      delete badge.dataset.gowinOriginalText;
    }

    badge.classList.remove(CARD_BADGE_CLASS);
    badge.removeAttribute('aria-label');
  }

  function updateCardDiscountBadges(scope) {
    const cards = [];

    if (scope instanceof Element && scope.matches('.card-wrapper')) {
      cards.push(scope);
    }

    scope.querySelectorAll?.('.card-wrapper').forEach((card) => cards.push(card));

    cards.forEach((card) => {
      const priceElement = card.querySelector('.price--on-sale');
      const badge = findSaleBadge(card);

      if (!priceElement || priceElement.classList.contains('price--sold-out')) {
        restoreCardBadge(badge);
        return;
      }

      const percentage = getDiscountPercentage(priceElement);

      if (!percentage || !badge) {
        restoreCardBadge(badge);
        return;
      }

      if (!badge.dataset.gowinOriginalText) {
        badge.dataset.gowinOriginalText = badge.textContent.trim();
      }

      badge.classList.add(CARD_BADGE_CLASS);
      badge.setAttribute('aria-label', `${percentage}% de descuento`);

      const nextText = `-${percentage}%`;
      if (badge.textContent.trim() !== nextText) badge.textContent = nextText;
    });
  }

  function restoreOriginalSaleBadge(priceElement) {
    const originalBadge = priceElement.querySelector(
      '.price__badge-sale[data-gowin-hidden-sale="true"]'
    );

    if (!originalBadge) return;

    originalBadge.style.removeProperty('display');
    originalBadge.removeAttribute('data-gowin-hidden-sale');
  }

  function updateProductDiscountBadges(scope) {
    const prices = [];

    if (
      scope instanceof Element &&
      scope.matches('.product__info-container .price')
    ) {
      prices.push(scope);
    }

    scope
      .querySelectorAll?.('.product__info-container .price')
      .forEach((price) => prices.push(price));

    prices.forEach((priceElement) => {
      const percentage = priceElement.classList.contains('price--on-sale')
        ? getDiscountPercentage(priceElement)
        : 0;
      let customBadge = priceElement.querySelector(`.${PRODUCT_BADGE_CLASS}`);

      if (!percentage) {
        customBadge?.remove();
        restoreOriginalSaleBadge(priceElement);
        return;
      }

      const originalBadge = priceElement.querySelector(
        `.price__badge-sale:not(.${PRODUCT_BADGE_CLASS})`
      );

      if (originalBadge) {
        originalBadge.style.display = 'none';
        originalBadge.setAttribute('data-gowin-hidden-sale', 'true');
      }

      if (!customBadge) {
        customBadge = document.createElement('span');
        customBadge.className = PRODUCT_BADGE_CLASS;
        customBadge.setAttribute('role', 'status');
        priceElement.appendChild(customBadge);
      }

      const nextText = `${percentage}% de descuento`;
      if (customBadge.textContent !== nextText) customBadge.textContent = nextText;
    });
  }

  function updateScope(scope) {
    const safeScope = scope?.querySelectorAll ? scope : document;
    prepareCards(safeScope);
    updateCardDiscountBadges(safeScope);
    updateProductDiscountBadges(safeScope);
  }

  function flushUpdates() {
    frameId = 0;
    const roots = Array.from(queuedRoots);
    queuedRoots.clear();

    if (roots.includes(document)) {
      updateScope(document);
      return;
    }

    roots.forEach(updateScope);
  }

  function scheduleUpdate(scope = document) {
    queuedRoots.add(scope);
    if (!frameId) frameId = window.requestAnimationFrame(flushUpdates);
  }

  function relevantRoot(node) {
    const element = node instanceof Element ? node : node?.parentElement;
    if (!element) return null;

    return element.closest(ROOT_SELECTOR) ||
      (element.matches(ROOT_SELECTOR) || element.querySelector?.(ROOT_SELECTOR)
        ? element
        : null);
  }

  function startObserver() {
    if (!document.body || !document.querySelector(ROOT_SELECTOR)) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const targetRoot = relevantRoot(mutation.target);
        if (targetRoot) scheduleUpdate(targetRoot);

        mutation.addedNodes.forEach((node) => {
          const root = relevantRoot(node);
          if (root) scheduleUpdate(root);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    ensureStyles();
    updateScope(document);
    startObserver();
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init, { once: true })
    : init();

  document.addEventListener('shopify:section:load', (event) => {
    scheduleUpdate(event.target);
  });

  document.addEventListener('shopify:section:reorder', () => {
    scheduleUpdate(document);
  });

  document.addEventListener('variant:change', (event) => {
    scheduleUpdate(
      event.target?.closest?.('.product__info-container') || document
    );
  });
})();
